// Aracı kargo firması API soyutlaması — yeni aracı firmalar (ör. başka danışmanlıklar) bu arayüzü uygular.

export type CargoBrokerType = 'fest' | 'deposel' | 'soyalp'

/** Cari API kimlik bilgisi (şifreli saklanır, çözülmüş hali). */
export interface CargoCreds {
  /** Webpostman kullanıcı adı / e-posta — `From` header. */
  username: string
  /** API anahtarı — `Authorization` header. */
  apiKey: string
}

/** Gönderi oluşturma girdisi (sağlayıcıdan bağımsız; FEST alanlarına eşlenir). */
export interface ConsignmentInput {
  /** Bizim ürettiğimiz tüm sistemde benzersiz barkod (FEST tarafında `musteribarkod`). */
  barcode: string
  /** Alıcı ad soyad. */
  customer: string
  /** Alıcı il. */
  provinceName: string
  /** Alıcı ilçe. */
  countyName: string
  /** Alıcı mahalle. */
  district?: string
  /** Alıcı adres. */
  address: string
  /** Alıcı telefon. */
  telephone?: string
  /** Varış şubesi kodu (FEST `branch_code`). */
  branchCode: string
  /** Tahsilat tutarı (TL) — kuruş "#.00" biçimine çevrilir. */
  amount?: number
  /** Ücret türü: 2=Alıcı Ödemeli, 3=Peşin/Kapıda Nakit, 6=Kapıda Kredi Kartı. */
  amountTypeId?: number
  /** Gönderi türü: 1=Koli, 2=Paket, ... */
  consignmentTypeId?: number
  /** İçerik özeti. */
  summary?: string
  /** Paket adedi. */
  quantity?: number
  /** Sipariş numarası. */
  orderNumber?: string
  /** Ağırlık — FEST'e "Kg" olarak gönderilir (doküman: "Kg olarak gönderiniz"; ör. 1 = 1 kg). */
  weight?: number
  /** Toplam desi. */
  totalBulk?: number
  /** Satıcı adı. */
  seller?: string
  /** Alıcı TC / Vergi No. */
  taxNumber?: string
  /** Gönderici notu. */
  senderNote?: string
  /** Alıcıya SMS gönderilsin mi. */
  sendSms?: boolean
}

/** Webpostman `amount_type_id` (tahsilat/ücret türü) değerleri. */
export const CARGO_AMOUNT_TYPE = {
  /** Alıcı ödemeli. */
  receiverPays: 2,
  /** Peşin / Kapıda Nakit. */
  cashOnDelivery: 3,
  /** Kapıda Kredi Kartı (kurye kapıda POS ile çeker). */
  cardOnDelivery: 6,
} as const

/**
 * Hedef şube "Kapıda Kredi Kartı" (amount_type_id=6) tahsilatını desteklemediğinde Webpostman
 * `consignment/add` bunu hata mesajıyla reddeder (ör. "Bu şube için Kapıda Kredi Kartı ile
 * tahsilat yapılamaz."). Bu durumda gönderi Kapıda Nakit'e (amount_type_id=3) düşürülüp yeniden
 * denenebilir. Mesaj cari/taşıyıcıya göre küçük farklılık gösterebildiğinden sabit metne değil,
 * "kredi kart" + "tahsilat" + "yapılam(a/ı)yor/-az" imzasına bakılır (TR locale ile küçültülür —
 * düz toLowerCase "İ"yi bozar). Yanlış pozitifi önlemek için üç parça da aranır.
 */
export function isCardCollectionUnsupported(message?: string): boolean {
  if (!message) return false
  const m = message.toLocaleLowerCase('tr').replace(/\u0307/g, '')
  return /kredi\s*kart/.test(m) && /tahsilat/.test(m) && /yap[ıi]lam/.test(m)
}

/** Gönderi oluşturma sonucu. */
export interface ConsignmentResult {
  success: boolean
  /** FEST'in döndürdüğü barkod (gönderi/postman barkod). */
  barcode: string | null
  /** FEST kayıt id'si. */
  recordId: string | null
  message: string
  raw?: unknown
  /** Kargo firması hız sınırı uyguladı (429) / host bekleme süresinde — istek atılmadı ya da reddedildi. */
  rateLimited?: boolean
  /** rateLimited ise bekleme süresi (ms). */
  retryAfterMs?: number
}

/** Tek bir kargo hareket/durum kaydı. */
export interface TrackingRecord {
  /** FEST iç gönderi no (`gonderino`/`kurcikno`, ör. 247607100006) — takip numarası DEĞİL,
   *  yalnız eşleşme/referans için. Müşterinin takip ettiği numara `trackingNo`dur. */
  gonderino: string
  /** Gerçek kargo (taşıyıcı) takip numarası — FEST `cikisno` (toplu takip) / `tedbarkod`
   *  (hareket dökümü), ör. 6460350247396. `cargo_tracking_code`a bu yazılır. */
  trackingNo: string
  /** Bizim gönderdiğimiz barkod (müşteri barkodu). */
  musteribarkod: string
  /** Ham FEST statü kodu (`statu_no`, ince kod: 00-60). */
  statusCode: string
  /** FEST durum kovası (`durum`, 0-6) — yalnız toplu takipte gelir (Haber Formları/Kayıp/Sorunlu tespiti için). */
  bucket?: string
  /** Durum açıklaması (FEST `sonuc`/`durum`). */
  statusText: string
  /** FEST tarafındaki sipariş no. */
  sipno?: string
  /** Gönderinin O AN bulunduğu şube adı (FEST `sube`, ör. "ARAS ADANA"). */
  currentBranch?: string
  /** Bulunduğu şube kodu (FEST `subekod`). */
  currentBranchCode?: string
  /** Bulunduğu şube telefonu (FEST `subetel`) — operatör aramada kullanabilir. */
  currentBranchPhone?: string
  raw?: unknown
}

/** Gönderi listesi (`/cargo`) sorgusu. Tarihler İstanbul takvim günü olarak gönderilir. */
export interface ShipmentListQuery {
  /** Kabul (alım) tarihi aralığı. */
  alimStart?: Date
  alimEnd?: Date
  /** Son hareket (değişim) tarihi aralığı — artımlı senkronun anahtarı. */
  degisimStart?: Date
  degisimEnd?: Date
  /** Durum kovası filtresi; varsayılan -1 (tümü). */
  durums?: number
}

export interface ShipmentListResult {
  /** API'nin bildirdiği toplam kayıt. */
  total: number
  /** Ham satırlar (bkz. parseListingRow). */
  rows: unknown[]
}

export interface CargoProvider {
  type: CargoBrokerType
  /** Gönderi açar (ağ çağrısı). Kısmi hata için throw etmez, yapılandırılmış sonuç döner. */
  createConsignment(input: ConsignmentInput, creds: CargoCreds): Promise<ConsignmentResult>
  /** Barkod listesiyle toplu durum sorgular (ağ çağrısı). */
  trackByBarcodes(barcodes: string[], creds: CargoCreds): Promise<TrackingRecord[]>
  /** Tarih aralığıyla hesabın gönderilerini listeler — tek istek (ağ çağrısı, opsiyonel). */
  listShipments?(query: ShipmentListQuery, creds: CargoCreds): Promise<ShipmentListResult>
  /** Tek gönderinin hareket dökümünü getirir (ağ çağrısı). */
  getMovements(barcode: string, creds: CargoCreds): Promise<TrackingRecord | null>
  /** Kimlik bilgilerini basit bir sorgu ile doğrular (opsiyonel). */
  testConnection?(creds: CargoCreds): Promise<{ ok: boolean; message: string }>
  /** Gönderi günceller (opsiyonel). */
  editConsignment?(barcode: string, input: ConsignmentInput, creds: CargoCreds): Promise<ConsignmentResult>
  /** Gönderi siler (opsiyonel). */
  deleteConsignment?(barcode: string, creds: CargoCreds): Promise<{ success: boolean; message: string }>
}

/** FEST statü kodu → açıklama (API dökümanından). */
export const FEST_STATUS: Record<string, string> = {
  '00': 'Kabul Bekliyor',
  '01': 'Kabul Edildi',
  '10': 'Teslim Edildi',
  '20': 'İade - İade Süreci Başlatıldı',
  '21': 'İade - Göndericiye İade Edildi',
  '22': 'İade - Kurye İade Sürecini Başlattı',
  '23': 'İade - İade Çıkış Şubesine Ulaştı',
  '24': 'İade - Şubeden İade Süreci Başlatıldı',
  '30': 'Teslim Edilemedi - Tekrar Dağıtım Planlanıyor',
  '40': 'Transfer Sürecinde',
  '41': 'Teslimat Şubesinde',
  '42': 'Kurye Dağıtımda',
  '50': 'Teslim Edilemedi',
  '60': 'Teslim Edilemedi - Teslimat Şubesinde',
}

/**
 * FEST statü kodunu kanonik 2 haneli biçime getirir. Bazı cariler kodu tek haneli döndürür
 * (ör. FIDANS `statu_no:"1"`), TELEAVM ise 2 haneli (`"01"`, `"40"`). Tek haneli sayısal kodu
 * baştan 0 ile doldurur → `"1"`→`"01"`, `"0"`→`"00"`. Böylece FEST_STATUS/eşleme haritaları tutar.
 */
export function normalizeFestStatusCode(code: string): string {
  return /^\d$/.test(code) ? `0${code}` : code
}

/** Aracı kargo senkronunun hedeflediği panel sipariş durumu adları. Modül açılınca tenant'a oluşturulur. */
export const CARGO_ORDER_STATUS = {
  problem: 'Sorunlu Kargo',
  returned: 'İade',
  delivered: 'Teslim Edildi',
  distributing: 'Dağıtımda',
  inTransit: 'Yolda',
} as const

/**
 * Sonuç metni (FEST `sonuc`) Dağıtımda/Yolda gösterse de operatörün müşteriyi araması gereken
 * durumlar → Sorunlu Kargo sayılır (kullanıcı tanımı). Metin normalize edilip (küçük harf +
 * boşluk sadeleştirme) bu kümeyle karşılaştırılır.
 */
export const PROBLEM_SONUC_TEXTS = new Set<string>([
  'köy dağıtımına tabi',
  'zimmet alındı',
  'zimmet edildi',
  'tercihli teslimat',
  'mobil bölge',
  // PTT: gönderi teslimat şubesinde bekliyor (statu_no 41). Ortalama 8-16 gün takılı kalıyor ve
  // müşterinin çoğu zaman haberi olmuyor → operatör arasın. (Aras'ın aynı koddaki "Varış
  // Şubesinde" durumu 2-3 günde çözülen normal akış olduğu için listede DEĞİL.)
  'ptt işyerinde bekliyor',
])

/**
 * Yalnız belirli statü kodlarıyla geldiğinde Sorunlu sayılan sonuç metinleri.
 * "Şubeden Alacak" (Aras): kargo hâlâ dağıtımdayken (30/41/42) müşteri gönderiyi şubeden
 * teslim almalı ama çoğu zaman haberi yok → operatör arasın. Aynı metin iade kodlarıyla
 * (20-24 veya kova 2) gelirse süreç zaten iadeye dönmüştür, aramaya gerek yok → İade kalır.
 */
export const PROBLEM_SONUC_TEXTS_BY_CODE: readonly { text: string; codes: readonly string[] }[] = [
  { text: 'şubeden alacak', codes: ['30', '41', '42'] },
]

/**
 * FEST `sonuc` alanı 20 karakterde KESİLİR ("PTT İşyerinde Bekliyor" → "PTT İşyerinde Bekliy").
 * Bu sınıra dayanmış metinlerde kuralın ön-eki olmak yeterli sayılır; daha kısa metinler tam
 * eşleşir. Aksi halde kesik metni kurala birebir yazmak gerekirdi (kırılgan).
 */
const FEST_SONUC_MAX_LEN = 20

/**
 * DİKKAT — Türkçe locale: düz `toLowerCase()` "İ" harfini `i` + U+0307 (birleşik nokta) yapar,
 * bu da normal `i` ile yazılmış kurala EŞLEŞMEZ ("PTT İşyerinde…" hiç tutmazdı). `tr` locale'i
 * doğru sonucu verir; U+0307 temizliği başka yoldan gelen bileşik biçimlere karşı emniyettir.
 */
function normalizeSonuc(text: string): string {
  return text.trim().toLocaleLowerCase('tr').replace(/\u0307/g, '').replace(/\s+/g, ' ')
}

function matchesSonuc(text: string, rules: Iterable<string>): boolean {
  const incoming = normalizeSonuc(text)
  if (!incoming) return false
  const maybeTruncated = text.trim().length >= FEST_SONUC_MAX_LEN
  for (const rule of rules) {
    if (rule === incoming) return true
    if (maybeTruncated && rule.startsWith(incoming)) return true
  }
  return false
}

function isProblemSonuc(text?: string, statusCode?: string, bucket?: string): boolean {
  if (!text) return false
  if (matchesSonuc(text, PROBLEM_SONUC_TEXTS)) return true
  // Koda bağlı kurallar: iade kovası (2) geldiyse uygulanmaz.
  if (!statusCode || bucket === '2') return false
  return PROBLEM_SONUC_TEXTS_BY_CODE.some(
    (rule) => rule.codes.includes(statusCode) && matchesSonuc(text, [rule.text]),
  )
}

/**
 * Kargo teslimat şubesinde, alıcının gelip almasını bekliyor mu (PTT "İşyerinde Bekliyor", Aras "Şubeden
 * Alacak", "Teslimat/Varış Şubesinde")? Şube teslim siparişlerinde bu beklenen durumdur, sorun değildir
 * (kullanıcı kararı 2026-10-09). "Bekleme Süresi Bitti" (iadeye dönüyor) bu kapsamda DEĞİL.
 */
export function isAtBranchPickupWait(statusText?: string | null): boolean {
  if (!statusText) return false
  const s = normalizeSonuc(statusText)
  return /şubeden alacak|işyerinde bekl|şubede bekl|teslimat şubesinde|varış şubesinde/.test(s)
}

export interface FestStatusOptions {
  /** Sipariş şube teslim (orders.branch_delivery_carrier dolu): şubede bekleme sorun sayılmaz. */
  branchDelivery?: boolean
}

/**
 * FEST statü/kova/sonuç → panel sipariş durumu adı. `null` = sipariş durumu değişmez.
 * `null` dönen kodlar: 00 Kabul Bekliyor ve 01 Kabul Edildi — FEST kabul etmiş ama fiziksel
 * taşıyıcı hareketi yok; sipariş "Kargoya Verildi"de kalır (kullanıcı kararı). Yalnız gerçek
 * hareket (40 Transfer → Yolda; 41/42/30 → Dağıtımda; 10 → Teslim; 20-24 → İade; 50/60 → Sorunlu).
 * Öncelik: Sorunlu Kargo > İade > Teslim Edildi > Dağıtımda > Yolda.
 * `bucket` yalnız toplu takipte gelir. `statusText` (FEST sonuc) verilirse belirli detay metinleri
 * Sorunlu'ya çeker: koşulsuz olanlar `PROBLEM_SONUC_TEXTS`, yalnız belirli kodlarla geçerli
 * olanlar `PROBLEM_SONUC_TEXTS_BY_CODE`.
 */
export function festToOrderStatusName(
  statusCode: string,
  bucket?: string,
  statusText?: string,
  options: FestStatusOptions = {},
): string | null {
  const s = statusCode
  const b = bucket ?? ''
  // Şube teslim siparişi şubede alıcıyı bekliyor: kayıp (kova 5) dışında sorun değil, dağıtımda sayılır.
  const branchWait = !!options.branchDelivery && b !== '5' && isAtBranchPickupWait(statusText)
  if (!branchWait && (['50', '60'].includes(s) || ['3', '5', '6'].includes(b) || isProblemSonuc(statusText, s, b))) return CARGO_ORDER_STATUS.problem
  if (['20', '21', '22', '23', '24'].includes(s) || b === '2') return CARGO_ORDER_STATUS.returned
  if (s === '10') return CARGO_ORDER_STATUS.delivered
  if (['41', '42', '30'].includes(s) || (branchWait && ['50', '60'].includes(s))) return CARGO_ORDER_STATUS.distributing
  if (s === '40') return CARGO_ORDER_STATUS.inTransit
  return null
}

/** Uygulama tarafı durum kategorisi (sipariş statüsü eşlemesi için). */
export type CargoStatusKind = 'pending' | 'in_transit' | 'delivered' | 'returned' | 'failed' | 'unknown'

/** Ham FEST statü kodunu uygulama kategorisine sınıflar. */
export function classifyFestStatus(code: string): CargoStatusKind {
  switch (code) {
    case '00':
      return 'pending'
    case '01':
    case '30':
    case '40':
    case '41':
    case '42':
      return 'in_transit'
    case '10':
      return 'delivered'
    case '20':
    case '21':
    case '22':
    case '23':
    case '24':
      return 'returned'
    case '50':
    case '60':
      return 'failed'
    default:
      return 'unknown'
  }
}
